import React, { createContext, useEffect, useReducer, useState } from "react";

import {
  ACCOUNT_INITIALISE,
  REGISTER,
  LOGIN,
  LOGOUT,
  OTP_VALIDATION_SUCCESS,
  OTP_VALIDATION_FAIL,
} from "../store/constants/constants";

import axios from "../utils/axios";

import accountReducer from "../store/reducers/accountReducer";

import Loader from "../components/Loader/Loader";
import useToast from "../hooks/customHook/useToast";

const initialState = {
  isRegistred: false,
  isLoggedIn: false,
  isOTPValidated: false,
  isInitialised: false,
  user: null,
  phoneNumber: "",
};

// The session token is an HttpOnly cookie managed by the API, so it is never
// stored or read here. Older builds kept it in localStorage - remove it.
const clearLegacyToken = () => {
  try {
    localStorage.removeItem("serviceToken");
  } catch (e) {
    // storage unavailable - nothing to clear
  }
};

const JWTContext = createContext({
  ...initialState,
  login: () => Promise.resolve(),
  validateOTP: () => Promise.resolve(),
  resendOTP: () => Promise.resolve(),
  register: () => Promise.resolve(),
  logout: () => Promise.resolve(),
});

const currentDate = new Date();
let threeMonthsBefore = new Date();
threeMonthsBefore.setMonth(currentDate.getMonth() - 3);

export const JWTProvider = ({ children }) => {
  const [state, dispatch] = useReducer(accountReducer, initialState);
  const [alignedKPI, setAlignedKPI] = useState([]);
  const [selectedKpi, setSelectedKpi] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [customerVisitpartyFilter, setCustomerVisitpartyFilterPartyFitler] =
    useState("");
  const [orderCollectionpartyFilter, setOrderCollectionPartyFitler] =
    useState("");
  const [partskartOrderPartyFilter, setPartskartOrderPartyFilter] =
    useState("");
  const [partskartLoginPartyFilter, setPartskartLoginPartyFilter] =
    useState("");
  const [partsKPIpartyType, setPartsKPIPartyType] = useState("");
  const [partsKPIPartCategory, setPartsKPIPartCategory] = useState("");
  const [filters, setFilters] = useState({
    zone: "",
    region: "",
    dealerCode: {},
  });
  const [applyFilters, setApplyFilters] = useState(false);
  const { errorToast, successToast } = useToast();

  const login = async (email, password) => {
    try {
      const response = await axios.post("/user/login", {
        password,
        email,
      });
      const { phoneNumber, status, message } = response.data;
      if (status === "ERROR") {
        errorToast(message);
        return;
      }
      dispatch({
        type: LOGIN,
        payload: {
          phoneNumber: phoneNumber,
        },
      });
    } catch (error) {
      errorToast(error.message || error);
      return;
    }
  };
  const validateOTP = async (payload) => {
    try {
      if (payload["triggerType"] === "login") {
        const response = await axios.post("/user/validate-otp", {
          trigger: payload["triggerType"],
          phone_number: payload["phoneNumber"],
          otp: payload["otp"],
        });
        if (response["data"] && response["data"]["status"] == "OK") {
          const { user } = response["data"];
          successToast("OTP validated successfully.");
          dispatch({
            type: OTP_VALIDATION_SUCCESS,
            payload: {
              user: user,
            },
          });
          let zone = "";
          if (user.user_role === "3") {
            zone = user.zone;
          } else if (user.user_role === "4") {
            zone = user.region[0];
          }

          let region = user.user_role === "4" ? user.region : "";

          let dealerCode =
            user.user_role === "6"
              ? { value: user.dealer_code, label: user.dealer_code }
              : "";
          setFilters({
            zone,
            region,
            dealerCode,
          });
        }
      } else if (payload["triggerType"] === "forgot_password") {
        const response = await axios.post("/user/validate-otp", {
          trigger: payload["triggerType"],
          passwordToken: payload["passwordToken"],
          otp: payload["otp"],
        });
        if (response["data"] && response["data"]["status"] == "OK")
          successToast(response["data"]["message"]);
        return true;
      }
    } catch (error) {
      errorToast(error.message || error);
      dispatch({ type: OTP_VALIDATION_FAIL });
      return false;
    }
  };
  const resendOTP = async (payload) => {
    try {
      let responseText = "",
        response;
      if (payload["triggerType"] === "forgot_password") {
        response = await axios.post("/user/trigger-otp", {
          trigger: payload["triggerType"],
          passwordToken: payload["passwordToken"],
        });
      } else if (payload["triggerType"] === "login") {
        response = await axios.post("/user/trigger-otp", {
          trigger: payload["triggerType"],
          phoneNumber: payload["phoneNumber"],
        });
      }
      if (response && response["data"] && response["data"]["status"] === "OK")
        responseText = response["data"]["message"];
      successToast(responseText);
    } catch (error) {
      errorToast(error.message || error);
      return;
    }
  };

  const register = async (username, email, phone_number, password) => {
    try {
      const response = await axios.post(`/user/register`, {
        username,
        email,
        phone_number,
        password,
      });
      const { status, message } = response.data;

      if (status === "ERROR") {
        return { status: "ERROR", message };
      }

      dispatch({
        type: REGISTER,
      });
      return {
        status: response.data.status,
        message: response.data.message,
      };
    } catch (error) {
      return {
        status: "ERROR",
        message: error?.message || "Internal server error",
      };
    }
  };

  const logout = async () => {
    try {
      // the API revokes the token and clears the cookie
      await axios.post("/user/logout");
    } finally {
      dispatch({ type: LOGOUT });
      setFilters({ zone: "", region: "", dealerCode: "" });
    }
  };

  useEffect(() => {
    const init = async () => {
      clearLegacyToken();
      try {
        // the session cookie is sent automatically; /user/me answers 401
        // when there is no valid session
        const response = await axios.get("/user/me");
        const { data, status } = response.data;

        if (status === "ERROR") {
          dispatch({
            type: ACCOUNT_INITIALISE,
            payload: {
              isLoggedIn: false,
              isOTPValidated: false,
              user: null,
            },
          });
          return;
        }

        dispatch({
          type: ACCOUNT_INITIALISE,
          payload: {
            isLoggedIn: true,
            isOTPValidated: true,
            user: data,
          },
        });

        let zone = "";
        let region = "";
        let dealerCode = "";

        if (data?.user_role === "3") {
          zone = data?.zone;
        } else if (data?.user_role === "4") {
          region = data?.region;
          zone = data?.region[0];
        }

        if (data?.user_role === "6") {
          dealerCode = { value: data?.dealer_code, label: data?.dealer_code };
        }

        setFilters({
          zone,
          region,
          dealerCode,
        });
        setAlignedKPI(data.kpi_list);
      } catch (err) {
        // no session (401) is the normal state on the login page
        dispatch({
          type: ACCOUNT_INITIALISE,
          payload: {
            isLoggedIn: false,
            isOTPValidated: false,
            user: null,
          },
        });
      }
    };

    init();
  }, []);

  if (!state.isInitialised) {
    return <Loader />;
  }

  return (
    <JWTContext.Provider
      value={{
        ...state,
        login,
        validateOTP,
        resendOTP,
        logout,
        register,
        applyFilters,
        setApplyFilters,
        filters,
        setFilters,
        alignedKPI,
        setAlignedKPI,
        selectedKpi,
        setSelectedKpi,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        customerVisitpartyFilter,
        setCustomerVisitpartyFilterPartyFitler,
        partsKPIpartyType,
        setPartsKPIPartyType,
        partsKPIPartCategory,
        setPartsKPIPartCategory,
        orderCollectionpartyFilter,
        setOrderCollectionPartyFitler,
        partskartOrderPartyFilter,
        setPartskartOrderPartyFilter,
        partskartLoginPartyFilter,
        setPartskartLoginPartyFilter,
      }}
    >
      {children}
    </JWTContext.Provider>
  );
};

export default JWTContext;
